'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, PropsWithChildren } from 'react';

import { formatTime } from '../formatTime';

type SubscriberCallback = (seconds: number) => void;
type TimerState = 'running' | 'paused' | 'stopped';
export interface TimerContextType {
  state: TimerState;
  start: (seconds?: number) => void;
  stop: () => void;
  reset: () => void;
  pause: () => void;
  resume: () => void;
  getElapsedSeconds: () => number;
  setElapsedSeconds: (seconds: number) => void;
  subscribe: (callback: SubscriberCallback) => string;
  unsubscribe: (id: string) => void;
  getFormattedTime: () => string;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};

export const useTimerSeconds = (): number => {
  const { subscribe, unsubscribe, getElapsedSeconds } = useTimer();
  const [seconds, setSeconds] = useState(getElapsedSeconds());
  useEffect(() => {
    const id = subscribe((s) => setSeconds(s));
    return () => unsubscribe(id);
  }, [subscribe, unsubscribe]);
  return seconds;
};

/**
 * Subscribes to the host's "app went to the background" signal and returns an unsubscribe. Injected
 * rather than assumed so core carries no platform lifecycle API: the web passes window blur/focus,
 * a native host passes AppState.
 */
export type BackgroundSubscription = (onBackground: () => void, onForeground: () => void) => () => void;

export interface TimerProviderProps {
  /**
   * Don't pause when the host goes to the background. The web sets this from a player preference,
   * and also throughout development, where losing focus usually means switching to an editor with
   * the puzzle still on screen. An app has neither reason: a backgrounded app's puzzle is gone.
   */
  disableAutoPause?: boolean;
  subscribeToBackground?: BackgroundSubscription;
}

export const TimerProvider: React.FC<PropsWithChildren<TimerProviderProps>> = ({
  children,
  disableAutoPause = false,
  subscribeToBackground,
}) => {
  const [state, setState] = useState<TimerState>('stopped');

  const secondsRef = useRef<number>(0);
  const subscribersRef = useRef<Record<string, SubscriberCallback>>({});

  const notifySubscribers = useCallback((seconds: number) => {
    Object.values(subscribersRef.current).forEach((callback) => callback(seconds));
  }, []);

  // Stop the timer when the host app goes to the background
  useEffect(() => {
    if (!subscribeToBackground) return;

    const onBackground = () => {
      if (!disableAutoPause) {
        pause();
      }
    };

    // Coming back deliberately does nothing: the player should see that the clock was stopped for
    // them while they were away, and start it again themselves.
    const onForeground = () => {};

    return subscribeToBackground(onBackground, onForeground);
  }, [state, subscribeToBackground, disableAutoPause]);

  useEffect(() => {
    let intervalId: any;

    if (state === 'running') {
      intervalId = setInterval(() => {
        secondsRef.current = secondsRef.current + 1;
        notifySubscribers(secondsRef.current);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [state, notifySubscribers]);

  const start = useCallback((seconds?: number) => {
    setState('running');
    if (seconds !== undefined) {
      secondsRef.current = seconds;
    }
  }, []);

  const stop = useCallback(() => {
    setState('stopped');
  }, []);

  const pause = useCallback(() => {
    if (state === 'running') {
      setState('paused');
    }
  }, [state]);

  const resume = useCallback(() => {
    if (state === 'paused') {
      setState('running');
    }
  }, [state]);

  const reset = useCallback(() => {
    secondsRef.current = 0;
    notifySubscribers(0);
  }, [notifySubscribers]);

  const getElapsedSeconds = useCallback(() => {
    return secondsRef.current;
  }, []);

  const setElapsedSeconds = useCallback(
    (seconds: number) => {
      secondsRef.current = seconds;
      notifySubscribers(seconds);
    },
    [notifySubscribers]
  );

  const subscribe = useCallback((callback: SubscriberCallback) => {
    const id = Math.random().toString(36).substring(7);
    subscribersRef.current[id] = callback;
    return id;
  }, []);

  const unsubscribe = useCallback((id: string) => {
    delete subscribersRef.current[id];
  }, []);

  const getFormattedTime = useCallback(() => {
    return formatTime(secondsRef.current);
  }, []);

  const value = {
    state,
    start,
    stop,
    reset,
    pause,
    resume,
    getElapsedSeconds,
    setElapsedSeconds,
    subscribe,
    unsubscribe,
    getFormattedTime,
  };

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
};
